import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { SurfaceCard } from "../ui/SurfaceCard";
import { tokens } from "../../theme";

export const axisTick = { fill: "rgba(255, 255, 255, 0.6)", fontSize: 12 };

export interface LegendKey {
  label: string;
  color: string;
  /** `line` for line series, `rect` for bars/areas. */
  shape?: "line" | "rect";
}

export const ChartLegend: React.FC<{ items: LegendKey[] }> = ({ items }) => (
  <Stack direction="row" alignItems="center" sx={{ flexWrap: "wrap", columnGap: { xs: 1.5, md: 2 }, rowGap: 0.5 }}>
    {items.map((item) => (
      <Stack key={item.label} direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={
            item.shape === "rect"
              ? { width: 10, height: 10, borderRadius: "3px", bgcolor: item.color }
              : { width: 14, height: 2, borderRadius: 1, bgcolor: item.color }
          }
        />
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.secondary" }}>
          {item.label}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

interface ChartCardProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  legend?: LegendKey[];
  action?: React.ReactNode;
  /** Plot height; the card grows around it (axis labels included). */
  height?: number | { [key: string]: number };
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  /** Render without the card surface (for nesting inside another card). */
  bare?: boolean;
}

/** Card frame shared by all charts: title row, legend/action, fixed plot height. */
export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  legend,
  action,
  height = { xs: 240, md: 300 },
  children,
  sx,
  bare = false,
}) => {
  const body = (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: { xs: 2, md: 2.5 } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 650, letterSpacing: "-0.01em", lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {(legend || action) && (
          <Stack
            direction="row"
            alignItems="center"
            sx={{ flexShrink: 0, columnGap: 2, rowGap: 1, flexWrap: "wrap", maxWidth: "100%" }}
          >
            {legend && <ChartLegend items={legend} />}
            {action}
          </Stack>
        )}
      </Stack>
      <Box sx={{ height, width: "100%", position: "relative", ml: -1 }}>{children}</Box>
    </>
  );

  if (bare) return <Box sx={sx}>{body}</Box>;
  return <SurfaceCard sx={sx}>{body}</SurfaceCard>;
};

/** Tooltip surface for Recharts `content` renderers. */
export const ChartTooltipFrame: React.FC<{ children: React.ReactNode; minWidth?: number }> = ({
  children,
  minWidth = 200,
}) => (
  <Box
    sx={{
      minWidth,
      p: 1.75,
      borderRadius: "16px",
      bgcolor: "rgba(20,20,22,0.86)",
      backdropFilter: "blur(20px) saturate(150%)",
      WebkitBackdropFilter: "blur(20px) saturate(150%)",
      border: `1px solid ${tokens.borderStrong}`,
      boxShadow: `${tokens.glass.highlight}, 0 24px 56px -16px rgba(0,0,0,0.9)`,
    }}
  >
    {children}
  </Box>
);

export default ChartCard;
